// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/index.ts
// Barrel export + factory de création du registry pré-configuré.
// ============================================================================

import { SourceRegistry }  from './SourceRegistry';
import { DeviceAcquirer }  from './acquirers/DeviceAcquirer';
import { ScreenAcquirer }  from './acquirers/ScreenAcquirer';
import { HlsAcquirer }    from './acquirers/HlsAcquirer';
import { RemoteAcquirer }  from './acquirers/RemoteAcquirer';
import { JingleAcquirer }  from './acquirers/JingleAcquirer';
import { SoundAcquirer }   from './acquirers/SoundAcquirer';
import { SceneAcquirer }   from './acquirers/SceneAcquirer';
import { FinalAcquirer }   from './acquirers/FinalAcquirer';

// ─── Re-exports ───────────────────────────────────────────────────────────────

export * from './types';
export { SourceRegistry }  from './SourceRegistry';
export type { RegisterOptions } from './SourceRegistry';

export { DeviceAcquirer }  from './acquirers/DeviceAcquirer';
export { ScreenAcquirer }  from './acquirers/ScreenAcquirer';
export { HlsAcquirer }    from './acquirers/HlsAcquirer';
export { RemoteAcquirer }  from './acquirers/RemoteAcquirer';
export { JingleAcquirer }  from './acquirers/JingleAcquirer';
export { SoundAcquirer }   from './acquirers/SoundAcquirer';
export { SceneAcquirer }   from './acquirers/SceneAcquirer';
export { FinalAcquirer }   from './acquirers/FinalAcquirer';

// ─── Types factory ────────────────────────────────────────────────────────────

export interface SourceRegistryAcquirers {
  device:  DeviceAcquirer;
  screen:  ScreenAcquirer;
  hls:     HlsAcquirer;
  remote:  RemoteAcquirer;
  jingle:  JingleAcquirer;
  sound:   SoundAcquirer;
  scene:   SceneAcquirer;
  final:   FinalAcquirer;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createSourceRegistry(): {
  registry:  SourceRegistry;
  acquirers: SourceRegistryAcquirers;
} {
  const registry = new SourceRegistry();

  const acquirers: SourceRegistryAcquirers = {
    device:  new DeviceAcquirer(),
    screen:  new ScreenAcquirer(),
    hls:     new HlsAcquirer(registry),  // ← registry passé pour injectStream après recreate
    remote:  new RemoteAcquirer(),
    jingle:  new JingleAcquirer(),
    sound:   new SoundAcquirer(),
    scene:   new SceneAcquirer(),
    final:   new FinalAcquirer(),
  };

  // Une piste caméra qui s'arrête est une panne : on marque la source en erreur
  // au lieu de la libérer, pour ne pas armer une boucle de réacquisition (cf.
  // reportFailure). La relance est manuelle, depuis la fenêtre de réglage du slot.
  acquirers.device.onTrackEnded = (sourceId, reason) => {
    registry.reportFailure(sourceId, reason);
  };

  acquirers.jingle.onEnded = (sourceId) => {
    registry.forceRelease(sourceId);
  };
  acquirers.sound.onEnded = (sourceId) => {
    registry.forceRelease(sourceId);
  };

  registry.registerAcquirer('device',  acquirers.device);
  registry.registerAcquirer('screen',  acquirers.screen);
  registry.registerAcquirer('hls',     acquirers.hls);
  registry.registerAcquirer('remote',  acquirers.remote);
  registry.registerAcquirer('jingle',  acquirers.jingle);
  registry.registerAcquirer('sound',   acquirers.sound);
  registry.registerAcquirer('scene',   acquirers.scene);
  registry.registerAcquirer('final',   acquirers.final);

  return { registry, acquirers };
}